import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, MapPin, CalendarBlank, UsersThree } from "@phosphor-icons/react";

const fmt = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (iso, n) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return fmtISO(d);
};

// Location filter -> single property slug (when a location has exactly one home)
const FORT_LAUDERDALE_SLUG = "pet-friendly-pool-oasis";

export default function BookingWidget() {
  const navigate = useNavigate();
  const [guests, setGuests] = useState(2);
  const [locSel, setLocSel] = useState("All locations");
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const today = fmtISO(new Date());
  const minCheckout = checkin ? addDays(checkin, 1) : today;

  // Checkout can never be before the check-in: bump it if needed.
  const onCheckin = (v) => {
    setCheckin(v);
    if (v && checkout && checkout <= v) setCheckout(addDays(v, 1));
  };

  // Deep-link from the Events calendar: /properties?checkin=YYYY-MM-DD&checkout=YYYY-MM-DD
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ci = p.get("checkin");
    const co = p.get("checkout");
    if (ci && co) {
      setCheckin(ci);
      setCheckout(co);
      // React Router doesn't natively scroll to hash after client-side nav.
      const t = setTimeout(() => {
        document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
      return () => clearTimeout(t);
    }
  }, []);

  // "Search" keeps guests on our own site (never the external storefront landing page):
  // it routes to the live booking widget for a single-home location, otherwise to the
  // Stays grid so the guest can pick a home — carrying any selected dates through.
  const handleSearch = () => {
    const params = new URLSearchParams();
    if (checkin && checkout) {
      params.set("checkin", checkin);
      params.set("checkout", checkout);
    }
    params.set("guests", String(guests));
    const qs = `?${params.toString()}`;
    if (locSel === "Fort Lauderdale, FL") {
      navigate(`/properties/${FORT_LAUDERDALE_SLUG}${qs}#book`);
      return;
    }
    // Always navigate (even when already on /properties) so the new dates land
    // in the URL and the property cards carry them through to the detail page.
    navigate(`/properties${qs}#stays`);
    setTimeout(() => {
      document.getElementById("stays")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div id="book" data-testid="booking-widget" className="scroll-mt-28 bg-sand border border-emerald/10 rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(15,26,21,0.35)]">
      <div className="flex items-center justify-between mb-5">
        <p className="overline text-sunset">Check availability</p>
        <span className="text-xs text-emerald-soft">Powered by Hospitable Direct</span>
      </div>
      {checkout && (
        <div data-testid="booking-preselected-dates" className="mb-5 flex items-center gap-2 rounded-full bg-sunset/10 border border-sunset/25 px-4 py-2 text-sm text-emerald w-fit">
          <CalendarBlank size={16} weight="fill" className="text-sunset" />
          Your dates: <span className="font-semibold">{fmt(checkin)} → {fmt(checkout)}</span> — pick a home below to book
        </div>
      )}
      <div className="grid md:grid-cols-[1.2fr_1fr_0.8fr_0.8fr_auto] gap-4 items-end">
        <label className="flex flex-col gap-2">
          <span className="overline text-emerald-soft flex items-center gap-1.5"><MapPin size={14} /> Location</span>
          <select value={locSel} onChange={(e) => setLocSel(e.target.value)} data-testid="booking-location" className="bg-transparent border-b border-emerald/25 py-2 text-emerald focus:border-sunset focus:outline-none transition-colors duration-300">
            <option>All locations</option>
            <option>Wilton Manors, FL</option>
            <option>Fort Lauderdale, FL</option>
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="overline text-emerald-soft flex items-center gap-1.5"><CalendarBlank size={14} /> Check-in</span>
          <input data-testid="booking-checkin" type="date" min={today} value={checkin} onChange={(e) => onCheckin(e.target.value)} className="bg-transparent border-b border-emerald/25 py-2 text-emerald placeholder:text-emerald-soft/60 focus:border-sunset focus:outline-none transition-colors duration-300" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="overline text-emerald-soft flex items-center gap-1.5"><CalendarBlank size={14} /> Check-out</span>
          <input data-testid="booking-checkout" type="date" min={minCheckout} value={checkout} onChange={(e) => setCheckout(e.target.value)} className="bg-transparent border-b border-emerald/25 py-2 text-emerald placeholder:text-emerald-soft/60 focus:border-sunset focus:outline-none transition-colors duration-300" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="overline text-emerald-soft flex items-center gap-1.5"><UsersThree size={14} /> Guests</span>
          <div className="flex items-center justify-between border-b border-emerald/25 py-2">
            <button data-testid="booking-guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="text-emerald-soft hover:text-sunset transition-colors duration-300 w-6">−</button>
            <span data-testid="booking-guests-value" className="text-emerald">{guests}</span>
            <button data-testid="booking-guests-plus" onClick={() => setGuests((g) => Math.min(12, g + 1))} className="text-emerald-soft hover:text-sunset transition-colors duration-300 w-6">+</button>
          </div>
        </label>
        <button
          type="button"
          data-testid="booking-search-btn"
          onClick={handleSearch}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-sunset px-7 py-3.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-emerald h-fit"
        >
          <MagnifyingGlass size={18} weight="bold" />
          Search
        </button>
      </div>
    </div>
  );
}
